import PropTypes from "prop-types";
import * as React from "react";
import { centredSquare, isLowResolution } from "../../domain/shopImageEditor";
import { toPixelCrop } from "../../domain/shopImages";
import { Slider, Spinner, WarningIcon } from "../../themeV2";
import CharacterDetailsFields from "./CharacterDetailsFields";
import CropPreview from "./CropPreview";
import css from "./CropStep.module.css";

const Cropper = React.lazy(() => import("react-easy-crop"));

const PORTRAIT = 3 / 4;

export default function CropStep({
  item,
  roles = [],
  onCrop,
  onDetailsChange,
}) {
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);

  const handleCropComplete = React.useCallback(
    (_area, areaPixels) => onCrop(toPixelCrop(areaPixels)),
    [onCrop],
  );

  return (
    <div className={css.root}>
      <div className={css.cropArea}>
        <React.Suspense fallback={<Spinner />}>
          <Cropper
            image={item.dataUrl}
            crop={position}
            zoom={zoom}
            aspect={PORTRAIT}
            initialCroppedAreaPixels={item.crop ?? undefined}
            onCropChange={setPosition}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </React.Suspense>
      </div>

      <Slider
        label="Zoom"
        value={zoom}
        min={1}
        max={3}
        step={0.1}
        onValueChange={setZoom}
      />

      {item.crop && (
        <div className={css.previews}>
          <CropPreview item={item} area={item.crop} caption="On cards" />
          <CropPreview
            item={item}
            area={centredSquare(item.crop)}
            caption="In circles"
            round
          />
        </div>
      )}

      {item.crop && isLowResolution(item.crop) && (
        <p role="status" className={css.warning}>
          <WarningIcon aria-hidden="true" className={css.warningIcon} />
          This image may look soft on high-resolution screens. You can still use
          it.
        </p>
      )}

      {item.kind === "character" && !item.recropOf && (
        <CharacterDetailsFields
          label={item.label}
          influencerCharacterId={item.influencerCharacterId}
          roles={roles}
          onDetailsChange={onDetailsChange}
        />
      )}
    </div>
  );
}

CropStep.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    kind: PropTypes.oneOf(["headshot", "character"]).isRequired,
    dataUrl: PropTypes.string.isRequired,
    imageSize: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }).isRequired,
    crop: PropTypes.object,
    label: PropTypes.string,
    influencerCharacterId: PropTypes.number,
    recropOf: PropTypes.number,
  }).isRequired,
  roles: PropTypes.arrayOf(PropTypes.object),
  onCrop: PropTypes.func.isRequired,
  onDetailsChange: PropTypes.func.isRequired,
};
